<template>
  <nav class="app-sidebar">
    <ul class="app-sidebar__nav">
      <li
        v-for="(item, index) in filteredItems"
        :key="index"
        class="app-sidebar__nav__item"
      >
        <template v-if="item.title">
          <SidebarNavTitle
            :name="item.name"
            :classes="item.class"
            :wrapper="item.wrapper"
          />
        </template>
        <template v-else-if="item.divider">
          <li class="app-sidebar__nav__divider" />
        </template>
        <template v-else>
          <template v-if="item.children">
            <SidebarNavDropdown
              :name="item.name"
              :url="item.url"
              :icon="item.icon"
              :badge="item.badge"
            >
              <template v-for="(child, cIndex) in item.children">
                <template v-if="child.children">
                  <SidebarNavDropdown
                    :key="cIndex"
                    :name="child.name"
                    :url="child.url"
                    :icon="child.icon"
                  >
                    <li
                      v-for="(subChild, ccIndex) in item.children"
                      :key="ccIndex"
                      class="app-sidebar__nav__item"
                    >
                      <SidebarNavLink
                        :name="subChild.name"
                        :url="subChild.url"
                        :icon="subChild.icon"
                        :badge="subChild.badge"
                        :external="subChild.external"
                      />
                    </li>
                  </SidebarNavDropdown>
                </template>
                <template v-else>
                  <li class="app-sidebar__nav__item" :key="cIndex">
                    <SidebarNavLink
                      :name="child.name"
                      :url="child.url"
                      :icon="child.icon"
                      :badge="child.badge"
                      :external="child.external"
                    />
                  </li>
                </template>
              </template>
            </SidebarNavDropdown>
          </template>
          <template v-else>
            <SidebarNavLink
              :name="item.name"
              :url="item.url"
              :icon="item.icon"
              :badge="item.badge"
              :external="item.external"
            />
          </template>
        </template>
      </li>
    </ul>
  </nav>
</template>

<script>
import navItems from '@sh/components/Layout/Sidebar/nav';
import SidebarNavTitle from '@sh/components/Layout/Sidebar/SidebarNavTitle.vue';
import SidebarNavLink from '@sh/components/Layout/Sidebar/SidebarNavLink.vue';
import SidebarNavDropdown from '@sh/components/Layout/Sidebar/SidebarNavDropdown.vue';
import { canAccess, unleashFeatures } from '@sh/helpers';

export default {
  name: 'Sidebar',
  components: {
    SidebarNavTitle,
    SidebarNavLink,
    SidebarNavDropdown,
  },
  data() {
    return {
      noPermissionItems: [],
      isUnleashResponse: false,
    };
  },
  computed: {
    filteredItems() {
      return navItems[this.$platform].filter((item) => !item.isUnleashProtected || (this.isUnleashResponse && !this.noPermissionItems.includes(item.url)));
    },
    hasTrackerAccess() {
      return this.$session.getUser().trackerAccess;
    },
  },
  async created() {
    unleashFeatures.forEach(async (item, index) => {
      if (!await canAccess(item)) {
        if (item.charAt(0) !== '/') {
          item = `/${item}`;
        }
        this.noPermissionItems.push(item);
      }
      this.isUnleashResponse = index === unleashFeatures.length - 1;
    });
  },
};
</script>

<style lang="scss">
.app-sidebar {
  padding: 0;
  color: $sidebar-link-color;
  width: 25rem;
  position: fixed;
  background: $sidebar-bg;
  border-right: .1rem solid $color-gray;
  transition: width .25s ease-in-out;

  &__nav {
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    width: 100%;
    list-style: none;
    font-size: 1.4rem;
    height: calc(100vh - 6rem);
    padding: 0;
    font-weight: 300;

    &::-webkit-scrollbar {
      background: transparent;
      border-radius: 1rem;
      width: .9rem;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 2.5rem;
      background: transparent;
    }

    &:hover::-webkit-scrollbar {
      background: $sidebar-scrollbar-hover-bg;
    }

    &:hover::-webkit-scrollbar-thumb {
      background: $sidebar-scrollbar-thumb-hover-bg;
    }

    &__title {
      padding: 1.4rem 1.8rem;
      font-size: 1.1rem;
      font-weight: 600;
      color: $black-400;
      text-transform: uppercase;
      display: flex;
      align-items: center;

      &::before {
        content: '';
        height: .1rem;
        width: 4.8rem;
        background: $black-300;
        margin-right: 2rem;
      }
    }

    &__divider {
      height: 1rem;
    }

    &__item {
      position: relative;
      margin: 0;
      transition: background .3s ease-in-out;

      ul {
        max-height: 0;
        padding: 0;
        margin: 0;
        overflow-y: hidden;
        transition: max-height .3s ease-in-out;

        li {
          padding: 0;
          list-style: none;
        }
      }
    }

    &__dropdown {
      .app-sidebar__nav__link {
        color: $sidebar-link-color;
        border-left: 0 !important;
        background: $sidebar-dropdown-link-bg;
      }

      .bi-chevron-down {
        position: absolute;
        right: 1.5rem;
        top: 1.7rem;
        pointer-events: none;
        transition: .2s;
        transform: rotate(0);
      }

      &.open {
        color: $color-white;
        background: $sidebar-link-hover-bg;

        .bi-chevron-down {
          transform: rotate(-180deg);
        }

        > ul, > ol {
          max-height: 100rem;
        }

        .nav-dropdown-toggle {
          background: $sidebar-link-hover-bg;

          .optimizer-icon {
            path, rect, circle {
              stroke: $color-white;

              &.fill {
                fill: $color-white;
              }
            }
          }
        }

        .app-sidebar__nav__link {
          color: $sidebar-link-color;

          &:hover {
            background: $sidebar-dropdown-link-hover-bg !important;
          }
        }


      }
    }

    &__link {
        display: block;
        padding: 1.2rem 1.8rem;
        color: $sidebar-link-color;
        text-decoration: none;
        cursor: pointer;

        &:hover,
        &.active {
          color: $sidebar-link-hover-color !important;
          text-decoration: none;
          background: $sidebar-link-hover-bg !important;

          svg {
            path, rect, circle {
              stroke: $sidebar-link-icon-hover-color !important;

              &.fill {
                fill: $sidebar-link-icon-hover-color !important;
              }
            }
          }
        }

        &.active {
          color: $sidebar-link-hover-color !important;
          background: $sidebar-link-hover-bg !important;

          svg {
            path, rect, circle {
              stroke: $color-light-blue !important;

              &.fill {
                fill: $color-light-blue !important;
              }
            }
          }

          &::before {
            content: '';
            width: .3rem;
            height: 100%;
            background: $color-light-blue;
            display: block;
            z-index: 10;
            position: absolute;
            top: 0;
            left: 0;
          }
        }

        i {
          display: inline-block;
          margin-top: -.6rem;
          vertical-align: middle;
          width: 2rem;
          text-align: center;
          margin-right: 1rem;
          pointer-events: none;

          svg {
            path, rect, circle {
              stroke: $sidebar-link-icon-color;

              &.fill {
                fill: $sidebar-link-icon-color;
              }
            }
          }
        }

        .badge {
          float: right;
          margin-top: .2rem;
        }
      }

      .nav-dropdown-toggle {
        display: block;
        padding: 1.2rem 1.8rem;
        color: $sidebar-link-color;
        text-decoration: none;
        // font-weight: 500;
        background: transparent;

        i {
          width: 2rem;
          color: $black-750;;
          text-align: center;
          margin-right: 1rem;
          pointer-events: none;

          svg {
            path, rect, circle {
              stroke: $sidebar-link-icon-color;

              &.fill {
                fill: $sidebar-link-icon-color;
              }
            }
          }
        }

        &:hover {
          color: $sidebar-link-color !important;
          text-decoration: none;
          background: $sidebar-link-hover-bg !important;
          cursor: pointer;

          svg {
            path, rect, circle {
              stroke: $sidebar-link-icon-hover-color !important;

              &.fill {
                fill: $sidebar-link-icon-hover-color !important;
              }
            }
          }
        }
      }
    }
  }

.sidebar-minimized {
  .hidden-cn {
    display: none;
  }

  .app-sidebar {
    z-index: 25;
    width: 5rem;
    background: $sidebar-bg;
    border-right: .1rem solid $color-gray;

    &:hover {
      overflow: visible;
    }

    &__nav {
      width: 5rem;
      overflow: visible;

      &__link.active::before {
        left: -1.5rem;
      }

      &__title, &__divider {
        display: none;
      }

      &__item {
        width: 5rem;
        overflow: hidden;
        border-left: 0 !important;

        ul {
          background: transparent;
        }

        .app-sidebar__nav__link {
          position: relative;
          padding-left: 0;
          margin: 0;
          white-space: nowrap;
          border-left: 0 !important;
          background: $sidebar-bg !important;

          &.nav-dropdown-toggle {
            display: inline;

            &::before {
              display: none;
            }

            i {
              width: 5rem;
            }

            svg {
              width: 2.2rem;
              height: 2.2rem;
              background: $color-white !important;
            }

            &:hover {
              background: $sidebar-bg;
            }
          }

          i {
            display: flex;
            float: left;
            width: 5rem;
            padding: 0;
            margin: 0 1.5rem 0 0 !important;
            font-size: 18px;
            background: $sidebar-bg !important;

            svg {
              width: 2.2rem;
              height: 2.2rem;
              background: $sidebar-bg !important;
            }
          }

          .badge {
            position: absolute;
            right: 15px;
            display: none;
          }

          &:hover {
            width: 25rem;
            color: $color-white !important;
            background: $sidebar-bg !important;

            .badge {
              display: inline;
            }
          }
        }

        ul {
          position: absolute;
          left: 5rem;

          li {
            position: relative;
            padding: 0;

            .app-sidebar__nav__link {
              width: 25rem;
            }

            ul, ol {
              display: block;
              position: absolute;
              top: 0;
              left: 100%;
            }
          }
        }

        .app-sidebar__nav__dropdown {
          // &.open {
            background: rgba(0,0,0, 0.7);
            > .nav-link i {
              color: rgba(16, 42, 85, 0.3);
            }
            > ul, > ol {
              display: inline;
            }
          // }
        }

        &:hover {
          width: 25rem;
          overflow: visible;
          background: $sidebar-bg;
          transition: 0s;

          > .app-sidebar__nav__link {
            width: 25rem;
          }

          > ul, > ol {
            display: inline !important;
            max-height: 1000rem;
            transition: 0s;

            li {
              width: 25rem;

              .app-sidebar__nav__link {
                width: 25rem;
              }
            }
          }

          .app-sidebar__nav__dropdown {
            // &.open {
              > ul, > ol {
                display: inline;
                max-height: 1000rem;
              }
            // }
          }
        }
      }

      .nav-dropdown-toggle {
        width: 25rem !important;
        padding-left: 0;
        pointer-events: none;
        background: $sidebar-bg;

        .bi-chevron-down {
          display: none;
        }

        i {
          width: 5rem;

          &.mediaManager svg{
            width: 2rem;
            height: 2rem;
          }

          svg {
            width: 2.2rem;
            height: 2.2rem;
            background: $sidebar-bg !important;
          }
        }

        &:hover {
          background: $sidebar-bg !important;

          .app-sidebar__nav__link {
            display: inline !important;
          }
        }
      }

      .app-sidebar__nav__dropdown__items {
        overflow: hidden;
        .app-sidebar__nav__item {
          width: 20rem !important;
          padding-left: 1.5rem;
          background: $sidebar-bg;

          i {
            display: none !important;
          }
        }
      }
    }
  }
}
</style>
